/**
 * @file redirect-view.ts
 * @description Подписи и подсказки «Сайт → SEO → Переадресации» (phase19.16,
 * партия Р3в, v0.19.64; SB131, SB132; uiux-r3 §3.5). Чистые функции — их
 * проверяют тесты, список и окно лишь рисуют.
 *
 *  - «Откуда взялась»: «авто: смена адреса 19.10», «вручную · Иван 17.10»;
 *  - статус «не действует: цель скрыта / удалена / адрес занят …»;
 *  - «Куда»: «О нас (/about-us)»;
 *  - подсказки к «Откуда» и «Куда» по общей таблице маршрутов shared
 *    (`classifySitePath`) — до запроса к серверу; окончательно решает api
 *    (живое содержимое знает только он);
 *  - полоса «Осталось N правил из 1000» с 900-го ручного правила.
 *
 * Виды цели — реестром `REDIRECT_TARGET_KINDS` (OCP): новый вид — запись.
 */

import { classifySitePath, isSafeSiteTargetPath, normalizeSiteRedirectPath } from "@chaiberi/shared";

import type { SiteRedirectEntityType, SiteRedirectItem, SiteRedirectSummary, SiteRedirectTarget } from "../api-site-redirects";
import { formatShopDate } from "../shop-time";
import type { PickListKey } from "../../hooks/site-builder/usePickList";

/** С какого числа ручных правил предупреждать (решение 6 плана Р3в). */
export const REDIRECT_WARN_FROM = 900;

/** Вид цели в окне «Добавить переадресацию». */
export type RedirectTargetKind = SiteRedirectEntityType | "PATH";

export interface RedirectTargetKindMeta {
  label: string;
  /** Список для выбора; нет — адрес вводится строкой. */
  list?: PickListKey;
}

export const REDIRECT_TARGET_KINDS: Readonly<Record<RedirectTargetKind, RedirectTargetKindMeta>> = {
  SITE_PAGE: { label: "Страница", list: "sitePages" },
  PRODUCT: { label: "Товар", list: "products" },
  CATEGORY: { label: "Категория", list: "categories" },
  CONTENT_ARTICLE: { label: "Статья", list: "articles" },
  CONTENT_CATEGORY: { label: "Рубрика блога", list: "contentCategories" },
  PATH: { label: "Адрес" },
};

/** Порядок вариантов в окне. */
export const REDIRECT_TARGET_ORDER: readonly RedirectTargetKind[] = ["SITE_PAGE", "PRODUCT", "CATEGORY", "CONTENT_ARTICLE", "CONTENT_CATEGORY", "PATH"];

/** «Откуда взялась»: авто — когда сменили адрес, вручную — кто и когда. */
export function redirectSourceText(item: Pick<SiteRedirectItem, "source" | "createdAt" | "createdByName" | "target">): string {
  const date = formatShopDate(item.createdAt).slice(0, 5);
  if (item.source === "AUTO") {
    const what = item.target.type === "PRODUCT" ? "товар" : "смена адреса";
    return `авто: ${what} ${date}`;
  }
  if (item.source === "IMPORT") return `из списка старого сайта · ${date}`;
  return `вручную${item.createdByName ? ` · ${item.createdByName}` : ""} ${date}`;
}

/** Статус «не действует: …»; действует — null. */
export function redirectStatusText(item: Pick<SiteRedirectItem, "status" | "liveOwner">): string | null {
  switch (item.status) {
    case "active":
      return null;
    case "target_hidden":
      return "не действует: цель скрыта";
    case "target_deleted":
      return "не действует: цель удалена";
    case "source_live":
      return item.liveOwner?.title ? `не действует: адрес занят «${item.liveOwner.title}»` : "не действует: адрес занят живой страницей";
    case "loop":
      return "не действует: получился круг переадресаций";
  }
}

/** «О нас (/about-us)»; адрес — сам адрес; удалённая цель — «цель удалена». */
export function redirectTargetText(target: SiteRedirectTarget): string {
  if (target.type === "PATH") return target.path ?? target.href ?? "—";
  if (!target.href && !target.title) return "цель удалена";
  if (target.title && target.href) return `${target.title} (${target.href})`;
  return target.title ?? target.href ?? "—";
}

/** Подсказка к «Откуда» до отправки; null — сервер решит сам. */
export function fromPathProblem(raw: string): string | null {
  const text = raw.trim();
  if (!text) return "Укажите адрес, с которого переадресовать.";
  const normalized = normalizeSiteRedirectPath(text);
  if (!normalized) return "Адрес на этом сайте должен начинаться с «/», например /old-page.";
  // Полный адрес: адреса других сайтов не подходят, свой — без домена (QA Р3в).
  if (normalized.host) return `Укажите путь без домена: ${normalized.path}. Адреса других сайтов не подходят.`;
  const cls = classifySitePath(text);
  if (cls.kind === "service") return `Адрес ${normalized.path} занят системой — переадресацию с него сделать нельзя.`;
  if (cls.kind === "section") return "Это раздел сайта, он открывается всегда — переадресация с него не нужна.";
  return null;
}

/**
 * Как адрес «откуда» сохранится, если ввод приводится заметно (`//evil.com`
 * → `/evil.com`, лишние «/» внутри); иначе null. Не запрет — сведение.
 */
export function fromPathNote(raw: string): string | null {
  const text = raw.trim();
  if (!text.includes("//") || /^https?:\/\//iu.test(text)) return null;
  const normalized = normalizeSiteRedirectPath(text);
  if (!normalized) return null;
  let shown = normalized.path;
  try {
    shown = decodeURI(normalized.path);
  } catch {
    // Показываем как есть.
  }
  return `Сохранится как ${shown}`;
}

/** Подсказка к «Куда» (адрес строкой); null — порядок. */
export function targetPathProblem(raw: string): string | null {
  const text = raw.trim();
  if (!text) return "Укажите адрес на этом сайте, например /catalog.";
  if (/^https?:\/\//iu.test(text)) return "Только адреса этого сайта: уберите домен — останется, например, /catalog.";
  if (!isSafeSiteTargetPath(text)) return "Адрес на этом сайте должен начинаться с «/».";
  if (classifySitePath(text).kind === "service") return "Вести на служебный адрес нельзя.";
  return null;
}

/** Совпадают ли «откуда» и «куда» после приведения (самое простое кольцо). */
export function isSelfRedirect(from: string, to: string): boolean {
  const a = normalizeSiteRedirectPath(from);
  const b = normalizeSiteRedirectPath(to);
  return !!a && !!b && a.path === b.path;
}

/** Полоса предела: с 900 ручных — сколько осталось; на пределе — отказ. */
export function redirectLimitNote(summary: SiteRedirectSummary | null): { text: string; full: boolean } | null {
  if (!summary) return null;
  const left = Math.max(0, summary.manualLeft);
  if (left === 0) return { text: `Лимит ${summary.manualLimit} правил; удалите неиспользуемые.`, full: true };
  if (summary.nearLimit || summary.manual >= REDIRECT_WARN_FROM) return { text: `Осталось ${left} из ${summary.manualLimit} ручных правил.`, full: false };
  return null;
}
